/**
 * Utility outage detail panel (Ice Storm Risk → "Power Outages"). A raw ingested
 * operational signal from the E.1.9/E.1.10 `utility_outages` table — NOT an
 * actionable incident (no R.3 dispatch actions): outages are ambient context that
 * paints the ice-storm impact picture (operator-cop-doctrine §4.5).
 *
 * Headlines the affected-customer count + status, with cause / crew / started /
 * restoration-ETA detail and an ingest-provenance footer (source + when we last
 * refreshed the row). Colour is always paired with the status label (§3A.7).
 */
import { useEffect, useState } from 'react';
import type { UtilityOutageFeature, UtilityOutageDetail } from '@tsr/shared-types';
import { fetchUtilityOutageDetail } from './api';
import { outageStyle } from './styles/outageStyles';
import { logAction } from './lib/operatorActions';

function agoLabel(iso: string | null): string {
  if (!iso) return 'unknown';
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return 'unknown';
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/** Absolute local time + relative, when we have a timestamp. */
function whenLabel(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleString()} · ${agoLabel(iso)}`;
}

/** A future ETA reads as a countdown; a past ETA reads as "overdue". */
function etaLabel(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const ms = d.getTime() - Date.now();
  const rel =
    ms >= 0
      ? `in ${Math.max(1, Math.round(ms / 60_000))}m`
      : `${agoLabel(iso)} (overdue)`;
  return `${d.toLocaleString()} · ${rel}`;
}

/** Title-case a canonical utility slug for display (hydro-quebec → Hydro Quebec). */
function utilityLabel(u: string): string {
  if (!/[a-z]/.test(u) || /\s/.test(u)) return u; // already a proper name (e.g. ENMAX, NB Power)
  return u
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function UtilityOutageBody({ outage }: { outage: UtilityOutageFeature }): JSX.Element {
  const p = outage.properties;
  const [lon, lat] = outage.geometry.coordinates;
  const style = outageStyle(p.status);
  const [detail, setDetail] = useState<UtilityOutageDetail | null>(null);

  useEffect(() => {
    logAction('view_utility_outage_detail', { outage_id: outage.id, utility: p.utility });
  }, [outage.id, p.utility]);

  useEffect(() => {
    const ctl = new AbortController();
    void fetchUtilityOutageDetail(outage.id, ctl.signal)
      .then(setDetail)
      .catch(() => undefined);
    return () => ctl.abort();
  }, [outage.id]);

  const customers = p.customers_affected;

  return (
    <div className="px-4 py-4 space-y-4" data-testid="utility-outage-body">
      <div className="border-l-2 pl-3" style={{ borderColor: style.color }}>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">Power outage</p>
        <h2 className="text-base font-semibold">{utilityLabel(p.utility)}</h2>
        <span
          className="inline-flex items-center gap-1.5 mt-1.5 text-[11px] px-2 py-0.5 rounded font-medium"
          style={{ backgroundColor: `${style.color}22`, color: style.color }}
        >
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ backgroundColor: style.color }}
          />
          {style.label}
        </span>
      </div>

      <section>
        <p className="text-2xl font-semibold tabular-nums">
          {customers != null ? customers.toLocaleString() : '—'}
          <span className="text-sm font-normal text-slate-400 ml-1.5">customers affected</span>
        </p>
      </section>

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Details</h3>
        <dl className="grid grid-cols-[7rem_1fr] gap-y-1 text-sm">
          <dt className="text-slate-400">Cause</dt>
          <dd>{p.cause || '—'}</dd>
          <dt className="text-slate-400">Crew assigned</dt>
          <dd>{p.crew_assigned == null ? 'unknown' : p.crew_assigned ? 'Yes' : 'No'}</dd>
          <dt className="text-slate-400">Started</dt>
          <dd className="text-xs">{whenLabel(p.started_at)}</dd>
          <dt className="text-slate-400">Restoration ETA</dt>
          <dd className="text-xs">{etaLabel(p.restoration_eta)}</dd>
          <dt className="text-slate-400">Reported</dt>
          <dd className="text-xs">{whenLabel(p.observed_at)}</dd>
          <dt className="text-slate-400">Position</dt>
          <dd className="font-mono text-xs">
            {lat.toFixed(3)}, {lon.toFixed(3)}
          </dd>
        </dl>
      </section>

      {p.has_service_area ? (
        <p className="text-[11px] text-slate-400 leading-relaxed">
          A published service-area footprint is drawn on the map (ground-clamped, beneath the
          marker).
        </p>
      ) : (
        <p className="text-[11px] text-slate-400 leading-relaxed">
          No published service-area footprint — the map shows an{' '}
          <span className="text-slate-300">approximate affected-area disc</span> (sized by
          customers affected) so the outage still reads as an area.
        </p>
      )}

      <footer className="border-t border-slate-800 pt-3 text-[10px] text-slate-500 leading-relaxed space-y-0.5">
        {detail?.properties.source && (
          <div>
            Source: {detail.properties.source}
            {detail.properties.source_record_id ? ` · #${detail.properties.source_record_id}` : ''}
          </div>
        )}
        {detail?.properties.ingested_at && <div>Ingested {agoLabel(detail.properties.ingested_at)}</div>}
        <div>
          Utility outage feeds — BC Hydro · Hydro-Québec · Hydro One · NS Power · municipal LDCs
          (Alectra / ENMAX / FortisAlberta / Hydro Ottawa / NB Power).
        </div>
      </footer>
    </div>
  );
}
